<template>
  <div class="advanced-management-products-prices-form">
    <!-- Price options in dialog -->
    <VuetifyDialog
      :content-component="priceOptionsDialogComponent"
      :content-component-props="priceOptionsDialogProps"
      title="Tipos de precios"
      :visible="priceOptionsDialogVisible"
      :hide-action-buttons="true"
      @onEventComponent="handlePriceOptionsDialogEvent"
    />
    <!-- Loading -->
    <VuetifyContentLoading :loading="processingRequest" />
    <template v-if="!processingRequest">
      <!-- VuetifyToolBar -->
      <VuetifyToolBar :title="toolBarTitle" />
      <!-- Content -->
      <v-container>
        <CardContainer>
          <v-form novalidate @submit.prevent="onSubmit">
            <!-- Calculadora -->
            <CardContainerElement width-text="4" width-content="8">
              <template v-slot:title>Aplicar cálculos</template>
              <template v-slot:subtitle>
                Selecciona la cantidad y el tipo de operación que deseas realizar en los precios de
                los productos seleccionados.
              </template>
              <template v-slot:content>
                <AdvancedManagementCalculator
                  ref="calculator"
                  @onClickApplyButton="handleApplyCalculation"
                />
              </template>
            </CardContainerElement>
            <!-- Tabla de products -->
            <CardContainerElement width-text="4" width-content="8">
              <template v-slot:title> Productos </template>
              <template v-slot:subtitle>
                Solo los productos seleccionados de la tabla, serán modificados en la categoría
                seleccionada.
              </template>
              <template v-slot:content>
                <AdvancedManagementProductsDataTable
                  :headers="headers"
                  :items="$v.formFields.items.$model"
                  @onChangeSelectedItems="handleChangeSelectedItems"
                  @onClickButtonEditPrices="handleClickButtonEditPrices"
                  @onItemPricesChanges="handleItemPricesChanges"
                />
              </template>
            </CardContainerElement>
            <!-- action buttons -->
            <FormButtons
              accept-button-text="Modificar"
              accept-button-type="button"
              :accept-button-loading="formProcessing"
              @onClickAcceptButton="handleAcceptButton"
              @onClickCancelButton="handleCancelButton"
            />
          </v-form>
        </CardContainer>
      </v-container>
    </template>
  </div>
</template>

<script>
// Constants
import { ACTION_TYPE } from '../constants'
// Components
import VuetifyDialog from '@/components/ui/VuetifyDialog'
import VuetifyToolBar from '@/components/ui/VuetifyToolBar'
import FormButtons from '@/components/ui/FormButtons'
import VuetifyContentLoading from '@/components/ui/VuetifyContentLoading'
import CardContainer from '@/components/ui/CardContainer'
import CardContainerElement from '@/components/ui/CardContainerElement'
import AdvancedManagementProductsDataTable from '../components/elements/AdvancedManagementProductsDataTable'
import AdvancedManagementPriceOptions from '../components/elements/AdvancedManagementPriceOptions'
import AdvancedManagementCalculator from '../components/elements/AdvancedManagementCalculator'
// Mixins
import formMixin from '@/mixins/formMixin'
import uiMixin from '@/mixins/uiMixin'
// Vuelidate plugin
import { validationMixin } from 'vuelidate'
import { required, minLength } from 'vuelidate/lib/validators'
// Services
import { getCategoryById } from '@/services/category'
import { getEveryDishesByCategoryId, updateDishById } from '@/services/dish'
// Vuex
import { mapGetters } from 'vuex'
// Utils
import { cloneDeep, isNil } from 'lodash'

export default {
  name: 'AdvancedManagementProductsCloningForm',
  components: {
    FormButtons,
    VuetifyContentLoading,
    VuetifyToolBar,
    VuetifyDialog,
    CardContainer,
    CardContainerElement,
    AdvancedManagementProductsDataTable,
    AdvancedManagementCalculator
  },
  mixins: [formMixin, uiMixin, validationMixin],
  data() {
    return {
      // Url params
      sourceCategoryId: this.$route.params.sourceCategory || null, // Desde donde extraigo los datos
      type: this.$route.params.type || ACTION_TYPE.category, // cloned type
      // Form
      formFields: {
        items: []
      },
      formFieldsValidations: {
        items: {
          prices: {
            withValues: 'No puedes dejar precios sin valor'
          }
        }
      },
      // Data table
      headers: [
        {
          text: 'Nombre',
          align: 'start',
          value: 'name',
          width: '40%'
        },
        {
          text: 'Precios',
          align: 'left',
          value: 'price',
          width: '60%'
        }
      ],
      // Diáĺogo de tipos de precios
      priceOptionsDialogVisible: false,
      priceOptionsDialogProps: null,
      // Others
      processingRequest: true,
      sourceCategoryData: null,
      sourceCategoryDishesData: [],
      selectedItems: []
    }
  },
  computed: {
    ...mapGetters('place', ['placeData']),
    /**
     * Obtiene el título de la ToolBar
     *
     * @return {string} - título toolbar
     */
    toolBarTitle() {
      return 'Modificar productos'
    },
    /**
     * Obtiene el componente de "opciones de precio"
     *
     * @return {Object}
     */
    priceOptionsDialogComponent() {
      return AdvancedManagementPriceOptions
    }
  },
  async mounted() {
    await this.getEveryNeededData()
  },
  methods: {
    /**
     * Show alert with error
     *
     * @param {string} error - error message
     */
    handleError(error) {
      this.modifyAppAlert({
        text: error,
        type: 'error',
        visible: true
      })
    },
    /**
     * When the user must click on accept button
     */
    handleAcceptButton() {
      // "Tocamos" todos los campos seleccionados
      const selectedItems = this.getSelectedDishes()

      // Repasamos los campos disponibles de la tabla y le
      // establecemos el error si es necesario
      if (selectedItems.length > 0) {
        this.formFields.items.forEach((item, index) => {
          const field = `items.$each.${index}.prices`
          if (selectedItems.find((selectedItem) => selectedItem.id === item.id)) {
            // Establecemos el error
            this.$set(this.formFields.items[index], 'errors', this.checkFieldErrors(field))
          } else {
            // Reseteamos los errores
            this.$set(this.formFields.items[index], 'errors', [])
          }
        })
      }

      // Lanzamos formulario
      this.onSubmit()
    },

    /**
     * When the user must click on cancel button
     */
    handleCancelButton() {
      this.routerGoTo()
    },
    /**
     * Aplicamos cálculo de los productos seleccinados
     * cuando pulsamos en el botón "Aplicar"
     */
    handleApplyCalculation() {
      this.selectedItems.map((item) => {
        if (
          !isNil(item.prices) &&
          typeof item.prices === 'object' &&
          Object.keys(item.prices).length > 0
        ) {
          Object.entries(item.prices).map((price) => {
            price[1].price = this.$refs.calculator.runCalculation(price[1].price)
            return price
          })
        }
        return item
      })
    },
    /**
     * Tras pulsar sobre botón de "Editar" de opciones
     * de precios, abre "Dialog" con las opciones de los precios
     *
     * @param {Object} item - producto a editar
     */
    handleClickButtonEditPrices(item) {
      // Modificamos las propiedades pasadas al componente
      this.priceOptionsDialogProps = { item }
      // Muestra diálogo de opciones de precios
      this.priceOptionsDialogVisible = true
    },
    /**
     * Tras la edición del precio de alguno de los items
     * "tocamos" el campo para validar este
     *
     * @param {Object} - "id" (vuelidate) y "item" (datos)
     */
    handleItemPricesChanges({ id, item }) {
      this.touchField(id)
      // coloco los errores dentro del campo (item)
      // para mostrarlo en la tabla
      this.formFields.items[item.index].errors = this.checkFieldErrors(id)
    },
    /**
     * Se modifican los items seleccionados
     *
     * @param {Array} items - elementos seleccionados
     */
    handleChangeSelectedItems(items) {
      this.selectedItems = items
    },
    /**
     * Evento lanzado tras modificar las opciones de
     * precios de alguno de los items
     *
     * @param {Array} prices - opciones de precios
     */
    handlePriceOptionsDialogEvent(prices) {
      // Ocultamos diálogo de opciones de precios
      this.priceOptionsDialogVisible = false

      // Tomamos el item que queremos editar
      const { item } = this.priceOptionsDialogProps
      // Buscamos dentro del array cuál es el elemento
      // seleccionado y modificamos valores
      const indexItem = this.formFields.items.findIndex((dish) => {
        return dish.id === item.id
      })

      // Colocamos los nuevos valores
      if (indexItem > -1) {
        this.formFields.items[indexItem].prices = prices
      }
    },
    /**
     * Obtiene todos los datos necesarios para la vista
     */
    async getEveryNeededData() {
      try {
        this.sourceCategoryData = await getCategoryById(this.sourceCategoryId)
        if (this.sourceCategoryData) {
          await this.setInitialProductsData(this.sourceCategoryData.id)
        } else {
          throw new Error('No se puede realizar el clonada de categorías indicado')
        }
      } catch (error) {
        // show error
        this.handleError(error.message)
        // Redirigimos al punto de donde venimos
        this.redirectToList()
      } finally {
        this.processingRequest = false
      }
    },
    /**
     * Obtenemos todos los datos de los items (productos)
     * que vamos a editar
     *
     * @param {Array} id - UID category
     */
    async setInitialProductsData(id) {
      // Platos de la categoría origen
      this.sourceCategoryDishesData = await getEveryDishesByCategoryId(id)
      // Items de la tabla
      this.formFields.items = cloneDeep(
        this.sourceCategoryDishesData.map((dish, index) => {
          return {
            index,
            id: dish.id,
            name: dish.name,
            prices:
              this.sourceCategoryId && dish.prices && dish.prices[this.sourceCategoryId]
                ? dish.prices[this.sourceCategoryId]
                : null
          }
        })
      )
    },
    /**
     * Is triggering after the form is correctly
     * validated by "Vuelidate"
     */
    async afterSubmit() {
      // Muestra alerta de clonación
      this.showQuestionAlert()
    },
    /**
     * Muestra diálogo de aprobación de la clonación
     */
    showQuestionAlert() {
      this.modifyAppAlert({
        actionButtonFn: async () => {
          try {
            // Loading...
            this.formProcessing = true

            // Añadimos (actualizamos) nuevos precios a los productos
            this.updateDishesPrices(this.sourceCategoryData.id)

            // Se indica que el proceso fue correcto
            this.modifyAppAlert({
              text: 'La modificación de los precio se realizó correctamente',
              visible: true
            })
            // Redirigimos al punto de donde venimos
            this.redirectToList()
          } catch (error) {
            // show error
            this.handleError(error.message)
          } finally {
            this.formProcessing = false
          }
        },
        actionButtonText: 'Modificar',
        text: '¿Estas seguro de haber seleccionado los productos que deseas modificar?',
        type: 'warning',
        visible: true
      })
    },
    /**
     * Actualizamos los precios de los productos seleccionados
     * con la categoría indicada
     *
     * @param {string} categoryId - UID category
     */
    async updateDishesPrices(categoryId) {
      const selectedDishes = this.getSelectedDishes()

      return Promise.all(
        selectedDishes.map(async (item) => {
          const indexItem = this.sourceCategoryDishesData.findIndex((dish) => {
            return dish.id === item.id
          })

          await updateDishById({
            id: item.id,
            prices: {
              ...this.sourceCategoryDishesData[indexItem].prices,
              [categoryId]: item.prices
            }
          })
        })
      )
    },
    /**
     * Redirigimos a la categoría donde nos encontrábamos
     */
    redirectToList() {
      this.routerGoTo()
    },
    /**
     * Nos quedamos solo con los datos seleccionados por el usuario
     * a la hora de clonar los productos
     *
     * @return {Array} - items seleccionados
     */
    getSelectedDishes() {
      return this.selectedItems.reduce((sumSelectedItems, item) => {
        const indexItem = this.formFields.items.findIndex((formItem) => {
          return formItem.id === item.id
        })

        if (indexItem > -1) {
          sumSelectedItems.push(item)
        }

        return sumSelectedItems
      }, [])
    }
  },
  // Validations with Vuelidate
  validations() {
    const rules = {
      formFields: {
        items: {
          required,
          minLength: minLength(1),
          $each: {
            prices: {
              withValues: (value, field) => {
                // Solo validamos los campos seleccionados
                const selectedItems = this.getSelectedDishes()
                const mustBeValidated =
                  selectedItems.length > 0
                    ? selectedItems.find((item) => {
                        return item.id === field.id
                      })
                    : false
                return (
                  !mustBeValidated ||
                  isNil(value) ||
                  Object.values(value).every((price) => {
                    return price.price > 0
                  })
                )
              }
            }
          }
        }
      }
    }

    return rules
  }
}
</script>
